import { Button, useClickOutside } from '@consigli/facade';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const WEEK_STARTS_ON = 1; // Monday

const parseKey = (key: string): Date | null => {
  if (!key) return null;
  const parsed = new Date(`${key}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

interface DateRangePopoverProps {
  startDate: string;
  endDate: string;
  onSelect: (start: string, end: string) => void;
  active: boolean;
  label: string;
  locale: string;
}

export const DateRangePopover = ({
  startDate,
  endDate,
  onSelect,
  active,
  label,
  locale,
}: DateRangePopoverProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(parseKey(startDate) ?? new Date()),
  );
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | null>(null);

  useClickOutside({
    ref: containerRef,
    onClickOutside: () => setIsOpen(false),
    enabled: isOpen,
  });

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    [locale],
  );
  const triggerFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    [locale],
  );
  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const monday = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) => formatter.format(addDays(monday, i)));
  }, [locale]);

  const committedStart = parseKey(startDate);
  const committedEnd = parseKey(endDate);

  const [rangeStart, rangeEnd] = pendingStart
    ? [pendingStart, hovered ?? pendingStart]
    : [committedStart, committedEnd];
  const [lo, hi] =
    rangeStart && rangeEnd && rangeEnd < rangeStart
      ? [rangeEnd, rangeStart]
      : [rangeStart, rangeEnd];

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setPendingStart(null);
        setHovered(null);
        setViewMonth(startOfMonth(committedStart ?? new Date()));
      }
      return next;
    });
  };

  const handleDayClick = (day: Date) => {
    if (!pendingStart) {
      setPendingStart(day);
      setHovered(day);
      return;
    }
    const [start, end] = day < pendingStart ? [day, pendingStart] : [pendingStart, day];
    setPendingStart(null);
    setHovered(null);
    setIsOpen(false);
    onSelect(toKey(start), toKey(end));
  };

  const triggerLabel =
    committedStart && committedEnd
      ? `${triggerFormatter.format(committedStart)} – ${triggerFormatter.format(committedEnd)}`
      : label;

  const renderMonth = (monthDate: Date) => {
    const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: WEEK_STARTS_ON });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    return (
      <div className="w-56">
        <div className="mb-2 text-center text-sm font-semibold capitalize text-gray-900">
          {monthFormatter.format(monthDate)}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {weekdayLabels.map((wd) => (
            <div key={wd} className="text-center text-[10px] font-medium text-gray-400">
              {wd}
            </div>
          ))}
          {days.map((day) => {
            const inMonth = isSameMonth(day, monthDate);
            const isStart = lo && isSameDay(day, lo);
            const isEnd = hi && isSameDay(day, hi);
            const inRange =
              lo && hi && isWithinInterval(day, { start: lo, end: hi }) && !isStart && !isEnd;
            const isEndpoint = isStart || isEnd;

            return (
              <button
                key={toKey(day)}
                type="button"
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => pendingStart && setHovered(day)}
                className={[
                  'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                  inMonth ? 'text-gray-900' : 'text-gray-300',
                  isEndpoint ? 'bg-brand font-semibold text-white' : '',
                  inRange ? 'bg-brand-light text-gray-900' : '',
                  !isEndpoint && !inRange ? 'hover:bg-gray-100' : '',
                ].join(' ')}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <Button
        {...(active ? { primary: true } : { secondary: true })}
        rounded
        onClick={handleToggle}
        type="button"
      >
        {triggerLabel}
      </Button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 rounded-lg border border-gray-200 bg-white p-4 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Previous month"
            >
              <FaChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Next month"
            >
              <FaChevronRight size={14} />
            </button>
          </div>
          <div className="flex gap-6">
            {renderMonth(viewMonth)}
            {renderMonth(addMonths(viewMonth, 1))}
          </div>
        </div>
      )}
    </div>
  );
};
